// Colocated Driver.js tour for AdminDashboard, targeted at super admins on
// their very first sign-in. Walks through the key parts of the Vault
// Dashboard, then navigates directly to /admin-details where the
// PartnerDetails super-admin setup tour picks up with the expense-tag /
// team / user walkthrough.

export const ADMIN_DASHBOARD_SUPER_ADMIN_INTRO_TOUR_ID = 'admin-dashboard-super-admin-intro-v9';

const ADMIN_DETAILS_ROUTE = '/admin-details';
const TAB_SWITCH_ANIMATION_MS = 200;

const clickTabAndAdvance = (tabSelector) => (element, step, opts) => {
    const tabButton = document.querySelector(tabSelector);
    if (tabButton) {
        tabButton.click();
        window.setTimeout(() => opts.driver.moveNext(), TAB_SWITCH_ANIMATION_MS);
    } else {
        opts.driver.moveNext();
    }
};

export const buildAdminDashboardSuperAdminIntroTourSteps = (navigate, { partnerNeedsSetup = true, superAdminHasWallet = true } = {}) => [
    {
        title: 'Welcome to Milipot',
        description: 'This is your vault dashboard, the command centre for your organisation\'s spending. Let us walk you through it in a few short steps.',
    },
    {
        element: '[data-tour="admin-dashboard-vault-balance"]',
        title: 'Your vault balance',
        description: 'This is the pool of funds your whole team can spend from. Every top-up you make lands here and it stays at zero until you add funds.',
        side: 'bottom',
        align: 'center',
    },
    {
        element: '[data-tour="admin-dashboard-refresh"]',
        title: 'Just topped up?',
        description: 'Tap this refresh icon and we will pull the latest figure so your vault balance catches up right away.',
        side: 'left',
        align: 'start',
    },
    {
        element: '[data-tour="admin-dashboard-top-up-form"]',
        title: 'Top up your vault',
        description: 'Type a phone number and an amount here to add funds to your vault. The number shown next to the form is your current vault balance.',
        side: 'left',
        align: 'start',
        onNextClick: clickTabAndAdvance('[data-tour="admin-dashboard-top-up-user-wallet-tab"]'),
    },
    {
        element: '[data-tour="admin-dashboard-top-up-form"]',
        title: 'Fund a user directly',
        description: 'Once your team is set up, send funds straight from your vault into a specific user\'s wallet so they can start spending. You will come back to this once you have added users on the next page.',
        side: 'left',
        align: 'start',
    },
    ...(!superAdminHasWallet ? [{
        title: 'Want to spend from Milipot yourself?',
        description: 'You do not need your own wallet to run the account. You can top up others from your vault and let them spend. If you also want to spend from Milipot yourself, activate your wallet from your <strong>profile page</strong>. You can do this whenever you are ready.',
    }] : []),
    {
        element: '[data-tour="admin-dashboard-top-up-form"]',
        title: 'Your PIN protects each top-up',
        description: 'You will be asked for your transaction PIN before the funds move. It is the same PIN you use for payouts. Five wrong tries in a row locks it for 30 minutes.',
        side: 'left',
        align: 'start',
    },
    {
        element: '[data-tour="admin-dashboard-stats"]',
        title: 'Activity at a glance',
        description: 'Top-ups, active users, outflows, and the current total sitting in your team\'s wallets. Swipe to flip between each.',
        side: 'bottom',
        align: 'center',
    },
    {
        element: '[data-tour="admin-dashboard-current-balances"]',
        title: 'Who has what to spend',
        description: 'Once your team has wallets, this list shows what each person can spend right now. Tap View all for the full list.',
        side: 'bottom',
        align: 'start',
    },
    {
        element: '[data-tour="admin-dashboard-top-spenders"]',
        title: 'Where the money is going',
        description: 'Once your team starts using their wallets, the biggest spenders show up here so you can see where the money is going. Filter by 7, 30, or 90 days, or view all time.',
        side: 'top',
        align: 'start',
    },
    {
        element: '[data-tour="admin-dashboard-latest-top-ups"]',
        title: 'Latest top-ups',
        description: 'Every top-up to your vault appears here with its status so you can confirm the money has landed. For older ones, head to the Ledger in the sidebar.',
        side: 'top',
        align: 'start',
    },
    {
        title: partnerNeedsSetup ? 'Let us set up your account' : 'Next',
        description: partnerNeedsSetup
            ? 'Before any of this becomes useful, you need at least one expense tag, one team, and one user. Tap Continue setup and we will take you to Organization (in the left sidebar) and walk you through each step. You can always come back here later from Dashboard.'
            : 'Let me take you to Organization in the left sidebar. That is where your expense tags, teams, and users live, and where you come back whenever you need to add a tag, team, or user.',
        nextBtnText: partnerNeedsSetup ? 'Continue setup' : 'Show me',
        onNextClick: (element, step, opts) => {
            opts.driver.destroy();
            navigate(ADMIN_DETAILS_ROUTE);
        },
    },
];
